class BaseError extends Error {
  cause?: unknown;

  constructor(message?: string, cause?: unknown) {
    super(message);

    this.cause = cause;

    // Check if Error.captureStackTrace is available before calling it
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    } else {
      console.warn(
        'Error.captureStackTrace is not available in this environment'
      );
    }
  }

  toString(): string {
    const message = this.message || '<no message>';

    const stackInfo = this.stack ? `\nStack: ${this.stack}` : '';

    const originalErrorInfo = this.cause ? `\nCause: ${this.cause}` : '';

    return `${this.name}: ${message}${stackInfo}${originalErrorInfo}`;
  }
}

export class UnreachableError extends BaseError {
  constructor(arg: never) {
    super('Unreachable error');
  }
}

export class HTTPResponseError extends BaseError {
  name = 'HTTP Response Error';
  public status: number;

  constructor(response: Response) {
    super(`${response.status} (${response.statusText})`);
    this.status = response.status;
  }
}

export class HTTPConnectionError extends BaseError {
  name = 'HTTP Connection Error';
}

export class EmailAlreadyInUseError extends BaseError {
  constructor() {
    super('Email already in use');
  }
}

export class EmailNotFoundError extends BaseError {
  constructor() {
    super('Email not found');
  }
}

export class DomainAlreadyInUseError extends BaseError {
  constructor() {
    super('Domain already in use');
  }
}

export class PassworMatchLoginError extends BaseError {
  constructor() {
    super('Password match login');
  }
}

export class WrongPasswordError extends BaseError {
  constructor() {
    super('Wrong password');
  }
}

export class CaptchaError extends BaseError {
  constructor() {
    super('Recaptcha error');
  }
}

export class NotImplementedError extends BaseError {
  constructor() {
    super('Not implemented error');
  }
}

export class UnauthorizedError extends BaseError {
  constructor() {
    super('Unauthorized error');
  }
}

export class ForbiddenError extends BaseError {
  constructor() {
    super('Forbidden error');
  }
}

export class TokenExpirationError extends BaseError {
  constructor() {
    super('Token is expired');
  }
}

export class InvalidDomainNameError extends BaseError {
  constructor() {
    super('Invalid domain name');
  }
}

export class TransactionNotFoundError extends BaseError {
  constructor() {
    super('Transaction not found');
  }
}

export class PartnerNotFoundError extends BaseError {
  constructor() {
    super('Partner not found');
  }
}

export class TariffUpdateError extends BaseError {
  constructor() {
    super('Failed to update not allowed');
  }
}

export class AdminSiteIsNotRespondingError extends BaseError {
  constructor(bodyText?: string) {
    super(bodyText || 'Admin site is not responding');
  }
}

export class OutOfResourcesError extends BaseError {
  constructor() {
    super('Failed to perform operation');
  }
}

export class RefundAmountExceedsTheBalance extends BaseError {
  constructor() {
    super('Amount exceeds the balance');
  }
}

export class MissingClientRequisites extends BaseError {
  constructor() {
    super('Missing client requisites');
  }
}

export class PromocodeNotFoundError extends BaseError {
  constructor() {
    super('Promocode not found');
  }
}

export class PromocodeAlreadyActivatedError extends BaseError {
  constructor() {
    super('Promocode already activated');
  }
}

export class MaximumAllowedFileSizeExceededError extends BaseError {
  constructor(file: File, maxSize: number) {
    super(`Maximum allowed file size exceeded ${file.size} > ${maxSize}`);
  }
}

export class ImageSizeViolationError extends BaseError {
  name = 'Image size violation';
}

export class InvalidEmailVerificationCodeError extends BaseError {
  name: string = 'Invalid email verification code';
}

export class InvalidPhoneVerificationCodeError extends BaseError {
  name: string = 'Invalid phone verification code';
}

export class UnexpectedError extends BaseError {
  name: string = 'Unexpected Error';
}

export class RewardAmountExceedsTheBalance extends BaseError {
  name: string = 'Reward amount exceeds the balance';
}

export class UserNotFoundError extends BaseError {
  name: string = 'User not found';
}

export class ClientSubscriptionIsCancelledError extends BaseError {
  name: string = 'Client subscription is cancelled';
}

export class ProdStackPollingAbortError extends BaseError {
  name: string = 'Prod stack polling aborted';
}

export class DebitAlreadyPaidError extends BaseError {
  constructor() {
    super('Debit already paid');
  }
}

export class MethodWorksOnlyForLastDebitError extends BaseError {
  constructor() {
    super('Method works only for last debit');
  }
}
